import React, { useRef, ReactNode, type MouseEvent } from 'react'
import { HStackContainer } from './styles/OneLineBtnHStack.styles'

interface OneLineBtnHStackProps {
  children: ReactNode
  disabled?: boolean
  onClick?: (e: MouseEvent<HTMLDivElement>) => void
  [key: string]: any // For additional props that get spread
}

const OneLineBtnHStack: React.FC<OneLineBtnHStackProps> = ({
  children,
  disabled,
  onClick,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) return

    e.stopPropagation()

    // Find all children that can be clicked
    if (containerRef.current) {
      const elements = containerRef.current.children

      Array.from(elements).forEach((element: Element) => {
        const htmlElement = element as HTMLElement

        if (
          !htmlElement.hasAttribute('disabled') &&
          !(htmlElement as any).disabled
        ) {
          // Try different methods to trigger click
          if (typeof (htmlElement as any).click === 'function') {
            ;(htmlElement as any).click()
          } else if (htmlElement.dispatchEvent) {
            htmlElement.dispatchEvent(
              new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
              }),
            )
          }
        }
      })
    }

    if (onClick) {
      onClick(e)
    }
  } // Update children cloning to pass disabled to all children
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        ...(child.props as any),
        disabled: disabled || (child.props as any)?.disabled,
      })
    }

    return child
  })

  return (
    <HStackContainer
      ref={containerRef}
      disabled={disabled}
      onClick={handleClick}
      {...props}
    >
      {childrenWithProps}
    </HStackContainer>
  )
}

export default OneLineBtnHStack
